import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectUser } from "../../../core/auth/authSlice";
import { selectMessages, setChats } from "../../../core/auth/chatSlice";
import { ChatApiRoutes } from "../../../shared/api-routes/chat.api-routes";
import { User } from "../../../shared/models";
import http from "../../../shared/utils/http";
import ChatBox from "../components/chat-box/ChatBox";
import MessagesHeader from "../components/messages-header/MessagesHeader";
import { IMessage } from "../components/message/Message";
import "./ChatList.scss";

const ChatList: React.FC = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { actorId } = useSelector(selectUser) as User;

	useEffect(() => {
		// Refetch chats whenever we return to the chat list
		http.get(ChatApiRoutes.GET_ALL_CHATS).then(async (res) => {
			const chatsArray = await Promise.all(
				(res.chats || []).map(async (chat: any) => {
					const lastMsg: IMessage = chat.lastMessage;

					// Check if last message should be filtered (system message where user is sender)
					const shouldFilterLastMessage = lastMsg && lastMsg.type === "system" && lastMsg.receiver !== actorId;

					if (shouldFilterLastMessage) {
						// Only fetch all messages if we need to find an alternative
						try {
							const messagesRes: any = await http.get(ChatApiRoutes.GET_MESSAGES_IN_CHAT(chat.id));
							let messages: IMessage[] = messagesRes.messages || [];

							// Sort messages by creation date to ensure newest is last
							messages = messages.sort((a, b) =>
								new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
							);

							// Find the last message that should be visible to this user (iterate backwards from newest)
							let validLastMessage = null;
							for (let i = messages.length - 1; i >= 0; i--) {
								const msg = messages[i];

								// Skip the filtered message
								if (msg.id === lastMsg.id) continue;

								// Check if this message is visible
								if (msg.type === "system") {
									// System messages are only visible if current user is the receiver
									if (msg.receiver === actorId) {
										validLastMessage = msg;
										break;
									}
								} else {
									// Regular messages are always visible
									validLastMessage = msg;
									break;
								}
							}

							return {
								id: chat.id,
								lastMessage: validLastMessage,
								other: chat.otherParticipant,
							};
						} catch (error) {
							console.error("Error fetching messages for chat:", chat.id, error);
							return {
								id: chat.id,
								lastMessage: null,
								other: chat.otherParticipant,
							};
						}
					}

					// If last message is visible (regular message or system message where user is receiver), show it
					return {
						id: chat.id,
						lastMessage: lastMsg,
						other: chat.otherParticipant,
					};
				})
			);
			dispatch(setChats(chatsArray));
		}).catch((error) => {
			console.error("Error fetching chats:", error);
		});
	}, [dispatch, location.pathname, actorId]);

	const chats = useSelector(selectMessages).chats;

	return (
		<div className="chatList">
			<MessagesHeader />
			{chats.map((chat, index) => (
				<ChatBox
					chatBoxInfo={chat}
					index={index}
					key={chat.id}
				/>
			))}
		</div>
	);
};

export default ChatList;
